import React, { useState } from 'react';
import { Play, Plus, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import LikeButton from './LikeButton';
import ShareButton from './ShareButton';
import LazyImage from './LazyImage';

const MovieCard = ({ movie, onPlay, onAddToList, onOpenDetails, onOpenShare, size = 'normal' }) => {
  const [isHovered, setIsHovered] = useState(false);

  const cardWidth = size === 'large' ? 'w-80' : size === 'small' ? 'w-48' : 'w-64';
  const aspectRatio = size === 'large' ? 'aspect-video' : 'aspect-[2/3]';

  return (
    <div 
      className={`${cardWidth} flex-shrink-0 cursor-pointer group transition-transform duration-300 hover:scale-105`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid={`movie-card-${movie.id}`}
    >
      <div className="relative">
        <LazyImage
          src={size === 'large' ? movie.backdrop : movie.poster}
          alt={movie.title}
          className={`${aspectRatio} rounded-lg overflow-hidden`}
          placeholderColor="#1a1a2e"
          blurAmount={15}
          transitionDuration={400}
          fallbackSrc="https://via.placeholder.com/400x600?text=No+Image"
        />
        
        {/* Hover overlay */}
        <div className={`absolute inset-0 bg-black/60 transition-opacity duration-300 rounded-lg ${
          isHovered ? 'opacity-100' : 'opacity-0'
        }`}>
          <div className="absolute inset-0 flex items-center justify-center">
            <Button
              variant="ghost"
              size="lg"
              onClick={() => onPlay(movie)}
              className="text-white hover:text-white bg-white/20 hover:bg-white/30 rounded-full w-16 h-16 active:scale-95 transition-transform"
              data-testid={`play-btn-${movie.id}`}
            >
              <Play className="w-8 h-8 fill-current" />
            </Button>
          </div>
        </div>

        {/* Movie info overlay */}
        {isHovered && (
          <div className="absolute -bottom-2 left-0 right-0 bg-gray-900 rounded-b-lg border border-gray-700 p-4 shadow-2xl z-10 transform translate-y-full">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={() => onPlay(movie)}
                  className="bg-white text-black hover:bg-gray-200 rounded-full w-8 h-8 p-0 active:scale-95"
                >
                  <Play className="w-4 h-4 fill-current" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onAddToList(movie)}
                  className="text-white hover:text-white border border-gray-600 hover:border-white rounded-full w-8 h-8 p-0 active:scale-95"
                >
                  <Plus className="w-4 h-4" />
                </Button>
                <LikeButton 
                  contentId={movie.id} 
                  initialLiked={movie.isLiked}
                  initialCount={movie.like_count || 0}
                  size="sm"
                  showCount={false}
                />
                <ShareButton 
                  content={movie}
                  size="sm"
                  onOpenShareModal={onOpenShare}
                />
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onOpenDetails(movie)}
                className="text-white hover:text-white border border-gray-600 hover:border-white rounded-full w-8 h-8 p-0 active:scale-95"
              >
                <ChevronDown className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="space-y-2">
              <h3 className="text-white font-medium text-sm line-clamp-1">{movie.title}</h3>
              <div className="flex items-center gap-2 text-xs">
                <Badge variant="outline" className="text-green-400 border-green-400">
                  98% Match
                </Badge>
                <span className="text-gray-400">{movie.rating}</span>
                <span className="text-gray-400">{movie.duration}</span>
              </div>
              <div className="flex flex-wrap gap-1">
                <Badge variant="secondary" className="text-xs bg-gray-700 text-gray-300">
                  {movie.genre}
                </Badge>
                {movie.isNetflixOriginal && (
                  <Badge className="text-xs bg-black/80 text-white border-0 flex items-center">
                    <span className="text-red-500">I</span>
                    <span className="text-orange-500">G</span>
                    <span className="text-yellow-500">o</span>
                    <span className="text-green-500">t</span>
                    <span className="text-blue-500 ml-1">S</span>
                    <span className="text-indigo-500">o</span>
                    <span className="text-purple-500">u</span>
                    <span className="text-pink-500">l</span>
                  </Badge>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MovieCard;
